import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ConfirmationProvider } from './context/ConfirmationContext';
import './index.css';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardLayout from './components/Layout/DashboardLayout';

// Admin Pages
import AdminDashboard from './pages/admin/Dashboard';
import ManageStudents from './pages/admin/ManageStudents';
import AdminStudentDetail from './pages/admin/StudentDetail';
import ManageLecturers from './pages/admin/ManageLecturers';
import AdminLecturerDetail from './pages/admin/LecturerDetail';
import AdminSubjects from './pages/admin/Subjects';
import AdminSubjectDetail from './pages/admin/SubjectDetail';
import AdminExams from './pages/admin/Exams';
import AdminExamDetail from './pages/admin/ExamDetail';
import AdminAttendance from './pages/admin/Attendance';
import AdminAttendanceDetail from './pages/admin/AttendanceDetail';
import AdminGrades from './pages/admin/Grades';
import GradeDetail from './pages/admin/GradeDetail';
import AdminReports from './pages/admin/Reports';

// Lecturer Pages
import LecturerDashboard from './pages/lecturer/Dashboard';
import LecturerSubjects from './pages/lecturer/Subjects';
import LecturerStudents from './pages/lecturer/Students';
import LecturerStudentDetail from './pages/lecturer/StudentDetail';
import LecturerExams from './pages/lecturer/Exams';
import LecturerExamDetail from './pages/lecturer/ExamDetail';
import LecturerAttendance from './pages/lecturer/Attendance';
import LecturerAttendanceDetail from './pages/lecturer/AttendanceDetail';
import LecturerReports from './pages/lecturer/Reports';
import LecturerProfile from './pages/lecturer/Profile';

// Student Pages
import StudentDashboard from './pages/student/Dashboard';
import StudentSubjects from './pages/student/Subjects';
import StudentSubjectDetail from './pages/student/SubjectDetail';
import StudentResults from './pages/student/Results';
import StudentAttendance from './pages/student/Attendance';
import StudentExams from './pages/student/Exams';
import StudentProfile from './pages/student/Profile';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.roleId)) {
    // Redirect to appropriate dashboard based on role
    const dashboards = { 1: '/admin', 2: '/lecturer', 3: '/student' };
    return <Navigate to={dashboards[user.roleId] || '/login'} replace />;
  }

  return children;
};

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      {/* Public Route */}
      <Route path="/login" element={<LoginPage />} />

      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={[1]}>
          <DashboardLayout><AdminDashboard /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/students" element={
        <ProtectedRoute allowedRoles={[1]}>
          <DashboardLayout><ManageStudents /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/students/:studentId" element={
        <ProtectedRoute allowedRoles={[1]}>
          <DashboardLayout><AdminStudentDetail /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/lecturers" element={
        <ProtectedRoute allowedRoles={[1]}>
          <DashboardLayout><ManageLecturers /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/lecturers/:lecturerId" element={
        <ProtectedRoute allowedRoles={[1]}>
          <DashboardLayout><AdminLecturerDetail /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/subjects" element={
        <ProtectedRoute allowedRoles={[1]}>
          <DashboardLayout><AdminSubjects /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/subjects/:subjectId" element={
        <ProtectedRoute allowedRoles={[1]}>
          <DashboardLayout><AdminSubjectDetail /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/exams" element={
        <ProtectedRoute allowedRoles={[1]}>
          <DashboardLayout><AdminExams /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/exams/:examId" element={
        <ProtectedRoute allowedRoles={[1]}>
          <DashboardLayout><AdminExamDetail /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/attendance" element={
        <ProtectedRoute allowedRoles={[1]}>
          <DashboardLayout><AdminAttendance /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/attendance/:sessionId" element={
        <ProtectedRoute allowedRoles={[1]}>
          <DashboardLayout><AdminAttendanceDetail /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/grades" element={
        <ProtectedRoute allowedRoles={[1]}>
          <DashboardLayout><AdminGrades /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/grades/:gradeId" element={
        <ProtectedRoute allowedRoles={[1]}>
          <DashboardLayout><GradeDetail /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/reports" element={
        <ProtectedRoute allowedRoles={[1]}>
          <DashboardLayout><AdminReports /></DashboardLayout>
        </ProtectedRoute>
      } />

      {/* Lecturer Routes */}
      <Route path="/lecturer" element={
        <ProtectedRoute allowedRoles={[2]}>
          <DashboardLayout><LecturerDashboard /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/lecturer/subjects" element={
        <ProtectedRoute allowedRoles={[2]}>
          <DashboardLayout><LecturerSubjects /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/lecturer/students" element={
        <ProtectedRoute allowedRoles={[2]}>
          <DashboardLayout><LecturerStudents /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/lecturer/students/:studentId" element={
        <ProtectedRoute allowedRoles={[2]}>
          <DashboardLayout><LecturerStudentDetail /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/lecturer/exams" element={
        <ProtectedRoute allowedRoles={[2]}>
          <DashboardLayout><LecturerExams /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/lecturer/exams/:examId" element={
        <ProtectedRoute allowedRoles={[2]}>
          <DashboardLayout><LecturerExamDetail /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/lecturer/attendance" element={
        <ProtectedRoute allowedRoles={[2]}>
          <DashboardLayout><LecturerAttendance /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/lecturer/attendance/:sessionId" element={
        <ProtectedRoute allowedRoles={[2]}>
          <DashboardLayout><LecturerAttendanceDetail /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/lecturer/reports" element={
        <ProtectedRoute allowedRoles={[2]}>
          <DashboardLayout><LecturerReports /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/lecturer/profile" element={
        <ProtectedRoute allowedRoles={[2]}>
          <DashboardLayout><LecturerProfile /></DashboardLayout>
        </ProtectedRoute>
      } />

      {/* Student Routes */}
      <Route path="/student" element={
        <ProtectedRoute allowedRoles={[3]}>
          <DashboardLayout><StudentDashboard /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/student/subjects" element={
        <ProtectedRoute allowedRoles={[3]}>
          <DashboardLayout><StudentSubjects /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/student/subjects/:subjectId" element={
        <ProtectedRoute allowedRoles={[3]}>
          <DashboardLayout><StudentSubjectDetail /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/student/results" element={
        <ProtectedRoute allowedRoles={[3]}>
          <DashboardLayout><StudentResults /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/student/attendance" element={
        <ProtectedRoute allowedRoles={[3]}>
          <DashboardLayout><StudentAttendance /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/student/profile" element={
        <ProtectedRoute allowedRoles={[3]}>
          <DashboardLayout><StudentProfile /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/student/exams" element={
        <ProtectedRoute allowedRoles={[3]}>
          <DashboardLayout><StudentExams /></DashboardLayout>
        </ProtectedRoute>
      } />

      {/* Default Route */}
      <Route path="/" element={
        user ? (
          <Navigate to={user.roleId === 1 ? '/admin' : user.roleId === 2 ? '/lecturer' : '/student'} replace />
        ) : (
          <Navigate to="/login" replace />
        )
      } />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <ConfirmationProvider>
          <AppRoutes />
        </ConfirmationProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
